import {
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { EditOutlined, SearchOutlined } from '@material-ui/icons'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { TEMPLATE_BLUE } from '../../const/color'
import { TOOLBAR_HEIGHT } from '../../const/layout'
import sendRequest from '../../utils/api'
import { debounced } from '../../utils/func'
import PaginationControlled from './components/PaginationControlled'
import { DOWNLOAD, EDITABLE, LIMIT, STATUS_DEFAULT } from './const'
import config from '../../config'

const styles = theme => ({
  root: {
    width: '100%',
    height: '100%',
  },
  toolbar: theme.mixins.toolbar,
  mainContain: {
    height: `calc(100% - ${TOOLBAR_HEIGHT}px)`,
    width: '100%',
    display: 'flex',
    padding: '20px',
  },
  filterBar: {
    display: 'flex',
    marginBottom: '20px',
  },
  cellHeader: {
    borderColor: '#878889',
    fontSize: '16px',
  },
  cellBody: {
    borderColor: '#e4e5e6',
    padding: 8,
  },
})

const PUBLISH_STATUS = {
  EDITABLE: 'Publish',
}

const getPublishStatus = template => {
  return PUBLISH_STATUS[template.productType] || 'Unpublish'
}

const CategoriesPage = ({ classes }) => {
  const [parentCategories, setParentCategories] = useState([])

  const [filterByStatus, setFilterByStatus] = useState(STATUS_DEFAULT)
  const [parentCategory, setParentCategory] = useState(-1)

  const [templates, setTemplates] = useState([])
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)

  const handlePagination = page => {
    fetchTemplate(parentCategory, query, page, filterByStatus)
  }

  const handleChangeQuery = value => {
    setQuery(value)
    fetchTemplate(parentCategory, value, 1, filterByStatus)
  }

  const handleChangeStatus = value => {
    setFilterByStatus(value)
    fetchTemplate(parentCategory, value, 1, value)
  }

  const handleChangeParentCategory = value => {
    setParentCategory(value)
    fetchTemplate(value, query, 1, filterByStatus)
    sendRequest({
      url: `/publisher/categories/${value}`,
      method: 'GET',
      data: { id: value },
    })
      .then(res => {
        const subCategoriesObj = res?.data?.data || {}
        const subCategories = Object.values(subCategoriesObj)
        if (subCategories.length > 0) {
          const totalItem = subCategories.shift()
          setTotal(totalItem.total)
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  const fetchTemplate = (parentCategory, query, page, filterByStatus) => {
    if (!parentCategory) {
      alert('Please choose parent category')
      return
    }

    let url = `/publisher/template?parentCategory=${parentCategory}&category=${parentCategory}`

    if (query) {
      url += `&query=${query}`
    }

    if (page) {
      url += `&page=${page}&limit=${LIMIT}`
    }

    if (filterByStatus && filterByStatus !== STATUS_DEFAULT) {
      url += `&productType=${filterByStatus}`
    }

    debounced(async () => {
      const result = await sendRequest({ url })
      setTemplates((result.data && result.data.data) || [])
    }, 500)
  }

  useEffect(() => {
    const getRootCategories = async () => {
      const res = await sendRequest({
        url: `${config.datalayerEndpoint}?v=1.0.1`,
        method: 'GET',
      })

      const { categories } = res.data
      const pCategories = Object.values(categories)
      setParentCategories(pCategories)

      setTimeout(() => {
        if (pCategories && pCategories.length > 0) {
          handleChangeParentCategory(pCategories[0].id)
        }
      }, 500)
    }

    getRootCategories()
  }, [])

  const generateTemplate = () => {
    return templates.map(template => (
      <TableRow key={`${template.id}-${template.title}`}>
        <TableCell className={classes.cellBody}>{template.id}</TableCell>
        <TableCell className={classes.cellBody}>{template.title}</TableCell>
        <TableCell className={classes.cellBody}>
          {getPublishStatus(template)}
        </TableCell>
        <TableCell className={classes.cellBody}>
          {moment(template.updatedTime).format('MM/DD/YYYY')}
        </TableCell>
        <TableCell className={classes.cellBody}>{template.updatedBy}</TableCell>
        <TableCell className={classes.cellBody}>
          {!!template?.aiEditorCategory && (
            <>
              <Button
                target="_blank"
                variant="contained"
                startIcon={<EditOutlined style={{ fontSize: 14 }} />}
                style={{ background: TEMPLATE_BLUE, color: 'white' }}
                onClick={() => {
                  const targetUrl = `${process.env.REACT_APP_AI_DOMAIN}/publisher?productId=${template.id}&category=${template.aiEditorCategory}`
                  window.location.href = targetUrl
                }}
              >
                Editor AI
              </Button>
              &nbsp;
            </>
          )}
          <Button
            target="_blank"
            variant="contained"
            startIcon={<EditOutlined style={{ fontSize: 14 }} />}
            style={{ background: TEMPLATE_BLUE, color: 'white' }}
            onClick={() => {
              const pCategory = parentCategories.find(
                c => c.id == parentCategory,
              )
              const url = `https://${pCategory.type}.${config.baseDomain}/${pCategory.slug}/publisher/products/${template.id}`
              window.location.href = url
            }}
          >
            Edit
          </Button>
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <div>
      <div className={classes.toolbar} />
      <div className={classes.mainContain}>
        <div style={{ flex: 1 }}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            All Templates
          </Typography>

          <div className={classes.filterBar}>
            <OutlinedInput
              endAdornment={
                <InputAdornment position="end">
                  <SearchOutlined />
                </InputAdornment>
              }
              value={query}
              onChange={event => handleChangeQuery(event.target.value)}
              placeholder="Search template with Title or ID or Editable Field..."
              inputProps={{ style: { padding: '10px', width: '400px' } }}
            />

            <FormControl
              variant="outlined"
              style={{ marginLeft: '20px', minWidth: '200px' }}
            >
              <Select
                value={filterByStatus}
                onChange={event => handleChangeStatus(event.target.value)}
              >
                <MenuItem value={STATUS_DEFAULT}>All</MenuItem>
                <MenuItem value={EDITABLE}>Publish</MenuItem>
                <MenuItem value={DOWNLOAD}>Unpublish</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              style={{ marginLeft: '20px', minWidth: '200px' }}
            >
              <Select
                value={parentCategory}
                onChange={event =>
                  handleChangeParentCategory(event.target.value)
                }
              >
                <MenuItem value="-1">Select Parent Category</MenuItem>
                {parentCategories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.cellHeader}>
                    Template ID
                  </TableCell>
                  <TableCell className={classes.cellHeader}>
                    Template Title
                  </TableCell>
                  <TableCell className={classes.cellHeader}>Status</TableCell>
                  <TableCell className={classes.cellHeader}>
                    Updated At
                  </TableCell>
                  <TableCell className={classes.cellHeader}>
                    Created By
                  </TableCell>
                  <TableCell className={classes.cellHeader} />
                </TableRow>
              </TableHead>

              <TableBody>{generateTemplate()}</TableBody>
            </Table>
            {total > 0 && templates.length > 0 && templates.length >= LIMIT && (
              <PaginationControlled
                total={total}
                handlePagination={handlePagination}
                query={query}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

CategoriesPage.propTypes = {
  category: PropTypes.object,
}

export default withStyles(styles)(withRouter(CategoriesPage))
